import { NavigationInstruction, Redirect, Router, RouterConfiguration } from 'aurelia-router';
import { Aurelia, autoinject, bindable, bindingMode } from 'aurelia-framework';
import { AuthService } from 'DataAccessLayer/Services/AuthService';
import { Config } from 'Config';
import { I18N } from 'aurelia-i18n';
import { ObjectHelper } from 'Code/Helpers/ObjectHelper';
import { StringHelper } from 'Code/Helpers/StringHelper';
import { SystemService } from 'DataAccessLayer/Services/SystemService';
import {AnalyticsService} from "./Code/Services/AnalyticsService";
import {AutoAnalyticsTracker} from "./Code/Services/AutoAnalyticsTracker";

@autoinject
export class App {
    public router: Router = null;
    public i18n: I18N = null;
    public authService: AuthService = null;
    public aurelia: Aurelia = null;
    public schema: Object = null;
    public schemaFormLogic: Object = null;
    public menu: Object = null;
    public progressImageLogo: string = null;
    public showWrappers: boolean = true;
    public userProfileName: string = '';
    private thisYear: number = new Date().getFullYear();

    private analyticsService: AnalyticsService = null;
    private autoTracker: AutoAnalyticsTracker = null;

    constructor(i18n: I18N, authService: AuthService, aurelia: Aurelia, analyticsService: AnalyticsService, autoTracker: AutoAnalyticsTracker) {
        this.i18n = i18n;
        this.authService = authService;
        this.aurelia = aurelia;
        this.analyticsService = analyticsService;
        this.autoTracker = autoTracker;
    }

    async bind() {
        SystemService.Instance.GetSchemaFormLogic();
        SystemService.Instance.GetSchemaLayout();
        SystemService.Instance.GetMenu();
        SystemService.Instance.GetUsersActivityStatus();
        this.schema = await SystemService.Instance.GetSchema();
        await SystemService.Instance.SetCacheIconsAndTitle(this.schema);

        let mode: string = Config.FetchModeFromUrl();
        if (mode === 's') {
            this.showWrappers = false;
        }

        // get user info and set default language
        let userInfo: any = this.authService.GetUserInfo();
        if (ObjectHelper.ObjectIs(userInfo) && StringHelper.StringIs(userInfo.UserName) && !userInfo.UserName.startsWith('PublicUser')) {
            if (this.router.currentInstruction.config.name == 'login') {
                this.router.navigateToRoute('dashboard');
            }
        }
        this.userProfileName = userInfo.ProfileName;
        let userLanguage = 'en';
        // check if user info contains language definition
        if (userInfo.hasOwnProperty('UserLanguage')) {
            // check if language is US english
            if (userInfo['UserLanguage'] == 'en-US') {
                // set full language code for US english
                userLanguage = userInfo['UserLanguage'];
            }
            else {
                // get first two letters from the language code
                userLanguage = userInfo['UserLanguage'].substring(0, 2);
            }
        }
        this.i18n.setLocale(userLanguage);
        moment.locale(userLanguage);
        Object.values(numbro.allLanguages).forEach(function (data) {
            numbro.registerLanguage(data);
        })
        numbro.setLanguage(userInfo['UserLanguage']);
        if (userInfo.hasOwnProperty('DateTimeFormat') && userInfo['DateTimeFormat'] > 0) {
            switch (userInfo['DateTimeFormat']) {
                case 1:
                    moment.updateLocale(userLanguage, {
                        longDateFormat: {
                            LT: 'H:mm',
                            LTS: 'H:mm:ss',
                            L: 'DD.MM. YYYY',
                            LL: 'D. MMMM YYYY',
                            LLL: 'D. MMMM YYYY H:mm',
                            LLLL: 'dddd D. MMMM YYYY H:mm',
                            l: 'D. M. YYYY'
                        }
                    });
                    break;
                case 2:
                    moment.updateLocale(userLanguage, {
                        longDateFormat: {
                            LTS: 'h:mm:ss A',
                            LT: 'h:mm A',
                            L: 'MM/DD/YYYY',
                            LL: 'MMMM D, YYYY',
                            LLL: 'MMMM D, YYYY h:mm A',
                            LLLL: 'dddd, MMMM D, YYYY h:mm A',
                            l: 'M/D/YYYY'
                        }
                    });
                    break;
            }
        }

        const calLocale = {
            de: {
                monthsShort: ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"],
                months: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
                daysShort: ["Son", "Mon", "Die", "Mit", "Don", "Fre", "Sam"],
                days: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
                cancel: "Zurück"
            },
            en: {
                monthsShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
                months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
                daysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
                days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                cancel: "Cancel"
            },
            cs: {
                monthsShort: ["Led", "Ún", "Bře", "Dub", "Kvě", "Čer", "Čec", "Srp", "Září", "Říj", "List", "Pro"],
                months: ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"],
                daysShort: ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"],
                days: ["Neděle", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"],
                cancel: "Zpět"
            },
            sk: {
                monthsShort: ["Jan", "Feb", "Mar", "Apr", "Máj", "Jún", "Júl", "Aug", "Sept", "Okt", "Nov", "Dec"],
                months: ["Január", "Február", "Marec", "Apríl", "Máj", "Jún", "Júl", "August", "September", "Október", "November", "December"],
                daysShort: ["Ne", "Po", "Ut", "St", "Št", "Pi", "So"],
                days: ["Nedeľa", "Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota"],
                cancel: "Zpět"
            }
        }
        this.progressImageLogo = Config.ProgressImageLogo();
    }

    attached() {
        this.autoTracker.initialize();
    }

    detached() {
        this.autoTracker.dispose();
    }

    configureRouter(routerConfiguration: RouterConfiguration, router: Router) {

        let index;
// route list
        let routeList = new Array<any>();
        // standard views
        routeList.push({ route: '', name: 'dashboard', moduleId: 'Instance/Default/Views/Static/Home/HomeView', nav: false, title: 'Bird Review' });
        routeList.push({ route: ':entityName/:filter?', name: 'entityList', moduleId: 'Instance/Default/Views/Dynamic/List/ListView', nav: false, replace: true, force: true });
        routeList.push({ route: ':entityName/create', name: 'entityCreate', moduleId: 'Instance/Default/Views/Dynamic/Create/CreateView', nav: false, replace: true, force: true });
        routeList.push({ route: ':entityName/create/:extData?', name: 'entityCreateWithExtData', moduleId: 'Instance/Default/Views/Dynamic/Create/CreateView', nav: false, replace: true, force: true });
        routeList.push({ route: ':entityName/edit/:entityId', name: 'entityEdit', moduleId: 'Instance/Default/Views/Dynamic/Edit/EditView', nav: false, replace: true, force: true });
        routeList.push({ route: 'aircraftDetail/:entityId', name: 'aircraftDetail', moduleId: 'Instance/Default/Views/Dynamic/AircraftDetail/AircraftDetailView', nav: false, replace: true, force: true });
        routeList.push({ route: 'operatorProfile/:entityId', name: 'operatorProfile', moduleId: 'Instance/Default/Views/Dynamic/OperatorProfile/OperatorProfileView', nav: false, replace: true, force: true });
        routeList.push({ route: 'login', name: 'login', moduleId: 'Login', nav: false, title: 'Login' });
        routeList.push({ route: 'register', name: 'register', moduleId: 'Register', nav: false, title: 'Register' });
        routeList.push({ route: 'error', name: 'error', moduleId: 'Instance/Default/Views/Static/Error/ErrorView', nav: false, title: 'Error | Bird Review' });
        routeList.push({ route: 'unauthorized', name: 'unauthorized', moduleId: 'Instance/Default/Views/Static/Unauthorized/UnauthorizedView', nav: false, title: 'Error | Bird Review' });
        routeList.push({ route: 'information', name: 'information', moduleId: 'Instance/Default/Views/Static/Information/InformationView', nav: false, title: 'Informace | Bird Review' });
        routeList.push({ route: 'dashboard', name: 'dashboard', moduleId: 'Instance/Default/Views/Static/Home/HomeView', nav: false, title: 'Bird Review' });
        routeList.push({ route: 'userProfile', name: 'UserProfile', moduleId: 'Instance/Default/Views/Static/UserProfile/UserProfileView', nav: false, title: 'User Profile | Bird Review' });
        // load standard views which will be customized
        let standardViews = Config.StandardViews();

        // check if exist any standard views
        if (ObjectHelper.IsNotNullAndUndefined(standardViews)) {

            // each views
            for (index in routeList) {

                // get current view
                let currentRoute = routeList[index];

                // try to find customized view
                let existStandardRoute = standardViews.filter((item) => item.hasOwnProperty('Name') && item['Name'] == currentRoute['name'])[0];

                // check f exist customized view
                if (ObjectHelper.IsNotNullAndUndefined(existStandardRoute)) {

                    // replace current view path
                    routeList[index]['moduleId'] = existStandardRoute['ModuleId'];
                }
            }
        }


        // load custom views if exists
        let customViews = Config.CustomViews();

        // check if exists any custom views
        if (ObjectHelper.IsNotNullAndUndefined(customViews)) {

            // each custom views
            for (index in customViews) {

                // get current custom view
                let currentView = customViews[index];

                // add new view route
                routeList.push({
                    route: currentView['Route'],
                    name: currentView['Name'],
                    moduleId: currentView['ModuleId'],
                    nav: currentView['Nav'],
                    title: null,
                    replace: true,
                    force: true,
                    settings: { icon: currentView['Icon'] }
                });
            }
        }

        //
        routerConfiguration.addPreRenderStep(PreRenderStep);
        routerConfiguration.addPreActivateStep(PreActivateStep)
        routerConfiguration.addPipelineStep('postcomplete', PostCompleteStep);

        // set routes
        routerConfiguration.map(routeList);

        // set router
        this.router = router;

        var defaultThemeMode = "light"; var themeMode; if (document.documentElement) {
            if (document.documentElement.hasAttribute("data-bs-theme-mode")) {
                themeMode = document.documentElement.getAttribute("data-bs-theme-mode");
            } else { if (localStorage.getItem("data-bs-theme") !== null) { themeMode = localStorage.getItem("data-bs-theme"); } else { themeMode = defaultThemeMode; } } if (themeMode === "system") { themeMode = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"; } document.documentElement.setAttribute("data-bs-theme", themeMode);
        }
        console.log(55555555);
    }
}

class PreActivateStep {
    async run(routingContext, next) {
        if (routingContext.hasOwnProperty('params') && routingContext.params.hasOwnProperty('entityName') && routingContext.config.hasOwnProperty('name') && routingContext.config.name == 'entityCreate') {
            let entityName = routingContext.params.entityName;
            let accessRight = await SystemService.Instance.GetAccessRightByEntity(entityName);


            if (ObjectHelper.IsNotNullAndUndefined(accessRight) && accessRight['Create'] != null && accessRight['Create'] == false) {
                return window.location.hash = '#/unauthorized';
            }
        }

        // show progress
        ($('#progress-overlayer-white') as any).fadeIn(50);

        return next();
    }
}


class PreRenderStep {
    run(routingContext, next) {
        return next();
    }
}

class PostCompleteStep {
    async run(routingContext, next) {

        // get body
        let body = ($('body') as any);

        // check if body has class "sidebar-open"
        if (body.hasClass('sidebar-open')) {

            // remove class "sidebar-open"
            body.removeClass('sidebar-open');
        }

        // hide progress
        ($('#progress-overlayer-white') as any).delay(500).fadeOut(100);

        // set scrollBar to top
        ($('div.wrapper') as any).scrollTop(0);

        return next();
    }
}
